<script setup lang="ts">
/**
 * #2123: the team Activity report. For a period (today, 7 days, 30 days) one row per teammate: jobs finished, and how
 * their time split between busy, waiting on you, waiting on another teammate or system, and idle, with the kit's
 * time bar. Below, the longest waits happening now, each with what it waits on and a link to the page that already
 * unblocks that kind of wait. `GET /v1/activity` decides every number; this page only shows them.
 *
 * Standard speaks plainly: no run, execution, gate, task, ID or cost. Advanced adds each teammate's run counts by
 * outcome, their cost for the period (the Spend page's own figures) and a link to their runs.
 *
 * The table is always labelled with the period its numbers are for. While another period loads, or after a read
 * fails, the last numbers stay on screen dimmed and say so; a failed read offers to try again.
 */
import {
    Avatar,
    AvatarFallback,
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhTimeSplit,
    ZhTimeSplitLegend,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
    type ActivityLongWait,
    type ActivityPeriod,
    type ActivityReport,
    type ActivityTeammate,
    activityApi,
} from '@/activity-api'
import { formatUsdCents } from '@/api'
import { useListState } from '@/composables/useListState'
import { useUiMode } from '@/composables/useUiMode'
import { formatActivityDuration } from '@/lib/activityDuration'

const PERIODS: ReadonlyArray<{ value: ActivityPeriod; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
]

/** The teammate's time columns, in the order the table shows them. */
const DURATION_COLUMNS = ['busyMs', 'waitingOnYouMs', 'waitingOnOtherMs', 'idleMs'] as const satisfies ReadonlyArray<
    keyof ActivityTeammate
>

/** What the link to each kind of wait says: plain in Standard, the page's own name in Advanced. */
const WAIT_LINKS: Record<ActivityLongWait['kind'], { standard: string; advanced: string; onYou: boolean }> = {
    decision: { standard: 'Decide', advanced: 'Open Gates', onYou: true },
    plan: { standard: 'Review the plan', advanced: 'Open Plans', onYou: true },
    stopped_run: { standard: 'Unblock', advanced: 'Open Blockers', onYou: true },
    open_work: { standard: 'Pick it up', advanced: 'Open Waiting', onYou: true },
    plan_limit: { standard: 'See your plan', advanced: 'Open Billing', onYou: false },
    team_off: { standard: 'See the team', advanced: 'Open Teams', onYou: false },
}

const { mode } = useUiMode()
const advanced = computed(() => mode.value === 'advanced')

const period = ref<ActivityPeriod>('7d')
/** #633: the period comes back on the next visit, in place before the first report read. */
const list = useListState('activity', {
    period: { ref: period, allowed: () => PERIODS.map(choice => choice.value) },
})
const report = ref<ActivityReport | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
/** Each read's number: a late answer for an earlier period never lands over a newer one. */
let latest = 0

async function load(): Promise<void> {
    const asked = ++latest
    loading.value = true
    try {
        const answer = await activityApi.report(period.value)
        if (asked !== latest) return
        report.value = answer
        error.value = null
    } catch {
        if (asked !== latest) return
        error.value = 'Could not load the report.'
    } finally {
        if (asked === latest) loading.value = false
    }
}

function choosePeriod(value: unknown): void {
    if (PERIODS.some(choice => choice.value === value)) period.value = value as ActivityPeriod
}

watch(period, () => load(), { immediate: true })

const teammates = computed(() => report.value?.teammates ?? [])
const waits = computed(() => report.value?.longestWaits ?? [])
const labelOf = (value: ActivityPeriod | undefined): string =>
    PERIODS.find(choice => choice.value === value)?.label ?? ''
/** The period picked, which may still be loading. */
const pickedLabel = computed(() => labelOf(period.value))
/** The period the numbers on screen are for: the table keeps this label until the new numbers arrive. */
const shownLabel = computed(() => labelOf(report.value?.period))
/** Another read is on its way while the last numbers stay on screen. */
const refreshing = computed(() => loading.value && report.value !== null)
/** A read failed while the last numbers stay on screen: they may be out of date. */
const stale = computed(() => error.value !== null && report.value !== null)

/** Up to two initials, for the avatar. */
function initials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean)
    return words
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('')
}

function exact(ms: number): string {
    const minutes = Math.floor(Math.max(0, ms) / 60_000)
    return `${minutes.toLocaleString()} minute${minutes === 1 ? '' : 's'}`
}

function splitLabel(row: ActivityTeammate): string {
    return [
        `Busy ${formatActivityDuration(row.busyMs)}`,
        `waiting on you ${formatActivityDuration(row.waitingOnYouMs)}`,
        `waiting on another teammate or system ${formatActivityDuration(row.waitingOnOtherMs)}`,
        `idle ${formatActivityDuration(row.idleMs)}`,
    ].join(', ')
}

function runsLink(memberId: string): string {
    return `/runs?member=${encodeURIComponent(memberId)}`
}

/** How long a wait has run, at the time the report was read. */
function waited(wait: ActivityLongWait): string {
    const end = report.value ? Date.parse(report.value.windowEnd) : Date.now()
    return formatActivityDuration(end - Date.parse(wait.since))
}

function linkText(wait: ActivityLongWait): string {
    const link = WAIT_LINKS[wait.kind]
    return advanced.value ? link.advanced : link.standard
}
</script>

<template>
    <div class="w-full min-w-0 space-y-6 p-4 md:p-8">
        <div class="flex flex-wrap items-end justify-between gap-3">
            <ZhPageTitle>Activity</ZhPageTitle>
            <label class="grid gap-1 text-sm">
                <span class="text-muted-foreground">Period</span>
                <Select :model-value="period" @update:model-value="choosePeriod">
                    <SelectTrigger class="min-w-32" aria-label="Period">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem v-for="choice in PERIODS" :key="choice.value" :value="choice.value">
                            {{ choice.label }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </label>
            <Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
        </div>

        <ZhPanel v-if="error" as="div" tone="failed">
            <div role="alert" class="flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="activity-error">
                <p>
                    {{ error }}
                    <template v-if="stale">
                        Showing the last report ({{ shownLabel }}), which may be out of date.
                    </template>
                </p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    :disabled="loading"
                    data-testid="activity-retry"
                    @click="load"
                >
                    Try again
                </Button>
            </div>
        </ZhPanel>

        <p v-if="refreshing" role="status" class="text-muted-foreground text-sm" data-testid="activity-refreshing">
            Loading {{ pickedLabel }}…
        </p>

        <div v-if="!report && loading" class="space-y-2" aria-busy="true">
            <Skeleton class="h-8 w-full" />
            <Skeleton class="h-8 w-full" />
            <Skeleton class="h-8 w-full" />
        </div>

        <ZhEmptyState v-else-if="report && teammates.length === 0">
            No teammates yet. Once you add teammates, this page shows how each one spends their time.
            <template #action>
                <RouterLink to="/members/add" class="text-sm underline-offset-4 hover:underline">Add a teammate</RouterLink>
            </template>
        </ZhEmptyState>

        <div
            v-else-if="report"
            class="space-y-6 transition-opacity"
            :class="{ 'opacity-60': refreshing || stale }"
            :aria-busy="refreshing"
            :data-stale="stale ? 'on' : 'off'"
            data-testid="activity-report"
        >
            <ZhTimeSplitLegend />
            <ZhPanel as="section" flush>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Teammate</TableHead>
                            <TableHead>Jobs finished</TableHead>
                            <TableHead>Busy</TableHead>
                            <TableHead>Waiting on you</TableHead>
                            <TableHead>Waiting on another teammate or system</TableHead>
                            <TableHead>Idle</TableHead>
                            <TableHead>Time split ({{ shownLabel }})</TableHead>
                            <template v-if="advanced">
                                <TableHead>Runs</TableHead>
                                <TableHead>Cost</TableHead>
                                <TableHead />
                            </template>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="row in teammates" :key="row.memberId">
                            <TableCell class="py-3">
                                <span class="flex items-center gap-2 font-medium">
                                    <Avatar size="sm">
                                        <AvatarFallback :aria-label="row.name">{{ initials(row.name) }}</AvatarFallback>
                                    </Avatar>
                                    {{ row.name }}
                                </span>
                            </TableCell>
                            <TableCell class="py-3 tabular-nums">{{ row.jobsFinished }}</TableCell>
                            <TableCell v-for="column in DURATION_COLUMNS" :key="column" class="py-3 tabular-nums">
                                <ZhTooltip :text="exact(row[column])">
                                    <span>{{ formatActivityDuration(row[column]) }}</span>
                                </ZhTooltip>
                            </TableCell>
                            <TableCell class="py-3">
                                <ZhTimeSplit
                                    :busy="row.busyMs"
                                    :waiting-on-you="row.waitingOnYouMs"
                                    :waiting-on-other="row.waitingOnOtherMs"
                                    :idle="row.idleMs"
                                    :label="splitLabel(row)"
                                />
                            </TableCell>
                            <template v-if="advanced">
                                <TableCell class="py-3 text-sm tabular-nums">
                                    {{ row.runs.succeeded }} succeeded · {{ row.runs.failed }} failed ·
                                    {{ row.runs.cancelled }} cancelled · {{ row.runs.started }} started
                                </TableCell>
                                <TableCell class="py-3 tabular-nums">{{ formatUsdCents(row.costUsdCents) }}</TableCell>
                                <TableCell class="py-3">
                                    <RouterLink :to="runsLink(row.memberId)" class="text-sm underline-offset-4 hover:underline">
                                        View runs
                                    </RouterLink>
                                </TableCell>
                            </template>
                        </TableRow>
                    </TableBody>
                </Table>
            </ZhPanel>

            <section class="space-y-3">
                <ZhSectionHeading>Longest waits now</ZhSectionHeading>
                <p v-if="waits.length === 0" class="text-muted-foreground text-sm">Nothing is waiting right now.</p>
                <ZhPanel v-else as="div" flush>
                    <ul class="divide-border divide-y">
                        <li
                            v-for="wait in waits"
                            :key="`${wait.memberId}:${wait.kind}:${wait.since}:${wait.waitingOn}`"
                            class="flex flex-wrap items-center gap-3 px-4 py-3 text-sm"
                        >
                            <ZhStatusPill :tone="WAIT_LINKS[wait.kind].onYou ? 'action' : 'neutral'" :pulse="false">
                                {{ WAIT_LINKS[wait.kind].onYou ? 'Waiting on you' : 'Waiting on another teammate or system' }}
                            </ZhStatusPill>
                            <span class="font-medium">{{ wait.teammate }}</span>
                            <span class="min-w-0 flex-1">{{ wait.waitingOn }}</span>
                            <ZhTooltip :text="`Since ${new Date(wait.since).toLocaleString()}`">
                                <span class="text-muted-foreground tabular-nums">for {{ waited(wait) }}</span>
                            </ZhTooltip>
                            <RouterLink :to="wait.link" class="underline-offset-4 hover:underline">
                                {{ linkText(wait) }}
                            </RouterLink>
                        </li>
                    </ul>
                </ZhPanel>
            </section>
        </div>
    </div>
</template>
